import {reportBrowserError} from "@mytour/platform";
import type {FileDocumentResponse, PrepareDocumentUploadPayload} from "@mytour/shared";
import {documentApiService} from "./documentApi.service";

export const DOCUMENT_UPLOAD_CONCURRENCY = 3;

export type DocumentUploadState =
  | "waiting"
  | "preparing"
  | "uploading"
  | "verifying"
  | "received"
  | "failed"
  | "unknown"
  | "missing-file"
  | "invalid";

export interface UploadFileDocumentItem {
  payload: PrepareDocumentUploadPayload;
  file?: File;
  document?: FileDocumentResponse;
}

export interface UploadFileDocumentsResult {
  successes: FileDocumentResponse[];
  failures: UploadFileDocumentItem[];
}

export type DocumentUploadStateCallback = (uploadId: string, state: DocumentUploadState) => void;

export async function uploadDocumentQueue(
  items: UploadFileDocumentItem[],
  onStateChange?: DocumentUploadStateCallback
): Promise<UploadFileDocumentsResult> {
  const uniqueItems = [...new Map(items.map((item) => [item.payload.uploadId, item])).values()];
  const results = new Map<string, FileDocumentResponse | null>();
  let nextIndex = 0;
  uniqueItems.forEach((item) => onStateChange?.(item.payload.uploadId, "waiting"));

  await Promise.all(
    Array.from({length: Math.min(DOCUMENT_UPLOAD_CONCURRENCY, uniqueItems.length)}, async () => {
      while (nextIndex < uniqueItems.length) {
        const item = uniqueItems[nextIndex++];
        if (!item) {
          continue;
        }
        results.set(item.payload.uploadId, await uploadOne(item));
      }
    })
  );

  return {
    successes: uniqueItems.flatMap((item) => {
      const document = results.get(item.payload.uploadId);
      return document ? [document] : [];
    }),
    failures: uniqueItems.filter((item) => !results.get(item.payload.uploadId)),
  };

  async function uploadOne(item: UploadFileDocumentItem): Promise<FileDocumentResponse | null> {
    const uploadId = item.payload.uploadId;
    function handleState(state: DocumentUploadState) {
      onStateChange?.(uploadId, state);
    }
    if (item.document?.uploadStatus === "received") {
      handleState("received");
      return item.document;
    }
    try {
      handleState("preparing");
      const prepared = await documentApiService.prepareUpload(item.payload);
      if (prepared.document.uploadStatus === "received") {
        handleState("received");
        return prepared.document;
      }
      if (!prepared.uploadUrl) {
        handleState(prepared.document.uploadStatus === "invalid" ? "invalid" : "unknown");
        return null;
      }
      if (!item.file) {
        handleState("missing-file");
        return null;
      }
      handleState("uploading");
      try {
        await documentApiService.uploadFileToScaleway(
          prepared.uploadUrl,
          item.file,
          prepared.uploadHeaders
        );
      } catch (error) {
        reportBrowserError(error, "document-upload");
      }
      handleState("verifying");
      const confirmed = await documentApiService.confirmUpload(uploadId);
      if (confirmed.uploadStatus === "received") {
        handleState("received");
        return confirmed;
      }
      handleState(
        confirmed.uploadStatus === "pending"
          ? "failed"
          : confirmed.uploadStatus === "invalid"
            ? "invalid"
            : "unknown"
      );
    } catch (error) {
      reportBrowserError(error, "document-upload");
      handleState("unknown");
    }
    return null;
  }
}
